(() => {
  'use strict';
  const idOf = value => String(value ?? '').split('/').pop();
  const storefrontHandle = pool => pool?.storefrontProductHandle || pool?.productHandle;
  const selectionFromEntries = (entries, config) => {
    const values = new Map(entries), id = idOf(values.get('id'));
    const v2 = config.schema === 2 && config.mode === 'native-inventory-v2';
    const stockPool = values.get('properties[_polygons_stock_pool]') || 'ready';
    const pool = config.pools.find(item => idOf(item.variantId) === id && (!v2 || item.stockPool === stockPool));
    if (!pool) throw new Error('This item is temporarily unavailable.');
    if (values.get('selling_plan')) throw new Error('This purchase option is unavailable.');
    const quantity = Number(values.get('quantity') || 1);
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('Choose a valid quantity.');
    const properties = {};
    for (const [name, value] of entries) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (!match) continue;
      if (typeof value !== 'string') throw new Error('This item’s attachment cannot be added here.');
      if (value) properties[match[1]] = value;
    }
    return { id, quantity, properties, acceptedDispatchDate: pool.dispatchDate, ...(v2 ? { stockPool, poolKey: pool.poolKey } : {}) };
  };
  const diFoldSelectionsFromEntries = (entries, detail, config, productHandle) => {
    if (!productHandle || detail?.productHandle !== productHandle || detail.inventoryConfirmed !== true
      || ![1, 3, 5].includes(detail.packSize) || !Array.isArray(detail.items) || !detail.items.length || detail.items.length > detail.packSize) {
      throw new Error('Please review your product selection.');
    }
    const grouped = new Map();
    let quantity = 0;
    for (const item of detail.items) {
      if (!/^(?:gid:\/\/shopify\/ProductVariant\/)?[1-9]\d*$/.test(String(item?.id ?? ''))
        || !Number.isSafeInteger(Number(idOf(item.id))) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > detail.packSize) {
        throw new Error('Choose a valid quantity.');
      }
      const id = idOf(item.id), pool = config?.pools?.find(value => idOf(value.variantId) === id);
      if (!pool || pool.productHandle !== productHandle || storefrontHandle(pool) !== productHandle
        || pool.pool !== 'ready' || pool.spoon !== false || pool.dispatchDate !== null) {
        throw new Error('This item is temporarily unavailable.');
      }
      quantity += item.quantity;
      const lineEntries = entries.filter(([name]) => name !== 'id' && name !== 'quantity');
      const selection = selectionFromEntries([...lineEntries, ['id', id], ['quantity', item.quantity]], config);
      const previous = grouped.get(id);
      if (previous) previous.quantity += selection.quantity; else grouped.set(id, selection);
    }
    if (quantity !== detail.packSize) throw new Error('Please review your product selection.');
    return [...grouped.values()];
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { selectionFromEntries, diFoldSelectionsFromEntries };
  if (typeof document === 'undefined') return;
  const config = window.PolygonsNativeCommerceConfig, api = window.PolygonsNativeCommerce, helper = window.PolygonsNativeCart;
  const context = window.PolygonsNativeContext;
  if (context?.hostname !== 'polygons-1.myshopify.com') return;
  let controller, busy = false;
  const shownSelections = new WeakMap();
  const cartRoot = (context?.root || '/').replace(/\/?$/, '/');
  const openCart = async (state, trigger) => {
    if (await window.PolygonsNativeDrawer?.open({ state, trigger })) return;
    location.assign(cartRoot + 'cart');
  };
  const notice = (form, text, alert = false) => {
    let output = form.querySelector('[data-native-purchase-notice]');
    if (!output) { output = document.createElement('p'); output.dataset.nativePurchaseNotice = ''; output.setAttribute('aria-live', 'polite'); form.append(output); }
    output.textContent = text; output.setAttribute('role', alert ? 'alert' : 'status');
  };
  const isProductForm = form => form?.tagName === 'FORM'
    && form.getAttribute('id') !== 'PolygonsProductForm'
    && /\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, window.location.href).pathname)
    && form.querySelectorAll('[type="submit"][name="add"],[type="submit"][data-bottle-add]').length > 0;
  const controls = (form, disabled) => form.querySelectorAll('[type="submit"],button[name="add"]').forEach(button => { button.disabled = disabled; });
  const diFoldRoot = form => {
    const root = form.closest?.('[data-difold-product]');
    return root?.matches?.('[data-difold-product]') ? root : null;
  };
  const isDiFoldMixed = form => Boolean(diFoldRoot(form)) && form.getAttribute('data-difold-main-form') === 'true';
  const isDiFoldAccessory = form => Boolean(diFoldRoot(form)) && form.getAttribute('data-difold-accessory-form') === 'true';
  const diFoldPlan = (form, entries) => {
    const root = diFoldRoot(form), product = window.PolygonsNativeProduct;
    const accessory = isDiFoldAccessory(form);
    if (!root || product?.handle !== context.productHandle || !accessory && root.dataset.difoldInventoryConfirmed !== 'true') {
      throw new Error('This item is temporarily unavailable.');
    }
    const mixed = isDiFoldMixed(form);
    if (mixed && typeof controller?.addMany !== 'function') throw new Error('This purchase option is temporarily unavailable.');
    const selections = mixed ? diFoldSelectionsFromEntries(entries, root.getDiFoldSelection?.(), config, context.productHandle)
      : [selectionFromEntries(entries, config)];
    const productHandle = accessory ? config.pools.find(item => idOf(item.variantId) === selections[0].id)?.productHandle : context.productHandle;
    if (selections.some(selection => {
      const pool = config.pools.find(item => idOf(item.variantId) === selection.id);
      return pool?.productHandle !== productHandle || storefrontHandle(pool) !== productHandle
        || pool.pool !== 'ready' || pool.spoon !== false || pool.dispatchDate !== null;
    })) throw new Error('This item is temporarily unavailable.');
    const relatedProducts = accessory ? [...root.querySelectorAll('[data-difold-related-product]')].map(node => JSON.parse(node.textContent)) : [];
    const products = [...new Map([product, ...(window.PolygonsNativePurchaseProducts || []), ...relatedProducts].filter(Boolean).map(value => [value.handle, value])).values()];
    const plan = api.planCart(api.createCatalog(config, products, context), selections, 'together');
    return { selections, plan };
  };
  const reportDiFold = (form, plan, error) => {
    if (isDiFoldAccessory(form)) {
      notice(form, error || `${new Intl.NumberFormat(context.locale || 'en', { style: 'currency', currency: context.currencyCode }).format(plan.merchandiseCents / 100)} for this selection.`, Boolean(error));
    } else diFoldRoot(form).dispatchEvent(new CustomEvent('difold:planchange', { detail: { plan, error } }));
  };
  const guidedCountryForms = new Set();
  for (const form of document.querySelectorAll('#PolygonsMarketForm, #NativeLabCountryForm')) {
    if (context?.pageType === 'cart') continue;
    const formId = form.getAttribute?.('id');
    if (!['PolygonsMarketForm', 'NativeLabCountryForm'].includes(formId) || guidedCountryForms.has(formId)
      || form?.tagName !== 'FORM' || !/\/localization\/?$/.test(new URL(form.action, window.location.href).pathname)
      || !form.querySelector('[name="country_code"]') || form.getClientRects && !form.getClientRects().length) continue;
    guidedCountryForms.add(formId);
    const help = document.createElement('p');
    help.className = 'native-fine'; help.style.cssText = 'font-size:13px;line-height:1.5;color:#666';
    help.textContent = 'Already started checkout? Confirm your delivery address there.';
    if (context?.pageType !== 'cart') {
      const link = document.createElement('a'); link.href = cartRoot + 'cart'; link.textContent = ' Review cart'; help.append(link);
    }
    form.append(help);
  }
  try { if (api && helper && context && config?.catalogMode === true) controller = helper.createBrowserController(api, config, context); } catch { /* The configured purchase path remains closed. */ }
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!isProductForm(form)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (!controller) { notice(form, 'This purchase option is temporarily unavailable.', true); return; }
    if (busy) return;
    busy = true; controls(form, true); notice(form, 'Adding to cart…');
    window.PolygonsNativeWallets?.invalidate();
    try {
      const entries = [...new FormData(form).entries()];
      if (diFoldRoot(form)) {
        const { selections, plan } = diFoldPlan(form, entries);
        reportDiFold(form, plan, null);
        const result = isDiFoldMixed(form) ? await controller.addMany(selections) : await controller.add(selections[0]);
        notice(form, result.recovered ? 'Your previous cart update is confirmed.' : 'Added to cart.');
        await openCart(result, event.submitter);
        return;
      }
      const selection = selectionFromEntries(entries, config);
      const shown = shownSelections.get(form);
      if (selection.acceptedDispatchDate && (shown?.id !== selection.id || shown?.acceptedDispatchDate !== selection.acceptedDispatchDate)) {
        notice(form, 'Choose a dispatch date on the product page before adding this item.', true);
        const pool = config.pools.find(item => idOf(item.variantId) === selection.id);
        if (storefrontHandle(pool)) location.assign(cartRoot + 'products/' + storefrontHandle(pool));
        return;
      }
      const result = await controller.add(selection);
      notice(form, result.recovered ? 'Your previous cart update is confirmed.' : 'Added to cart.');
      await openCart(result, event.submitter);
    } catch (cause) {
      const message = cause.message || 'Your cart could not be updated.';
      notice(form, message, true);
      if (diFoldRoot(form)) reportDiFold(form, null, message);
    }
    finally {
      busy = false;
      if (diFoldRoot(form)) {
        try { diFoldPlan(form, [...new FormData(form).entries()]); controls(form, false); } catch { controls(form, true); }
      } else controls(form, false);
    }
  }, true);
  const initializeProduct = async () => {
    if (!context.productHandle || context.pageType !== 'product') return;
    if (!controller) {
      for (const form of document.querySelectorAll('form[action*="/cart/add"]')) { if (isProductForm(form)) { controls(form, true); notice(form, 'This purchase option is temporarily unavailable.', true); } }
      return;
    }
    if (document.querySelector?.('polygons-bottle') && window.customElements?.whenDefined) await window.customElements.whenDefined('polygons-bottle');
    const product = window.PolygonsNativeProduct;
    const productIds = new Set((product?.variants || []).map(variant => idOf(variant.id)));
    const forms = [...document.querySelectorAll('form[action*="/cart/add"]')].filter(form => {
      if (!isProductForm(form)) return false;
      const id = idOf(form.querySelector('[name="id"]')?.value);
      return isDiFoldMixed(form) || isDiFoldAccessory(form) || productIds.has(id) || config.pools.some(pool => storefrontHandle(pool) === context.productHandle && idOf(pool.variantId) === id);
    });
    if (!forms.length) return;
    for (const form of forms.filter(form => diFoldRoot(form))) {
      const root = diFoldRoot(form);
      const update = () => {
        try {
          const { plan } = diFoldPlan(form, [...new FormData(form).entries()]);
          if (isDiFoldMixed(form)) root.dataset.difoldMixedReady = 'true';
          controls(form, busy);
          reportDiFold(form, plan, null);
        } catch (cause) {
          if (isDiFoldMixed(form)) root.dataset.difoldMixedReady = 'false';
          controls(form, true);
          reportDiFold(form, null, cause.message || 'Availability is temporarily unavailable.');
        }
      };
      root.addEventListener('difold:selectionchange', update);
      root.addEventListener('change', event => { if (event.target.form === form) update(); });
      root.addEventListener('input', event => { if (event.target.form === form && event.target.name === 'quantity') update(); });
      update();
    }
    const standardForms = forms.filter(form => !diFoldRoot(form));
    if (!standardForms.length) return;
    if (!config.pools.some(pool => storefrontHandle(pool) === context.productHandle)) {
      for (const form of standardForms) { controls(form, true); notice(form, 'This item is temporarily unavailable.', true); }
      return;
    }
    try {
      const products = [...new Map([product, ...(window.PolygonsNativePurchaseProducts || [])].filter(Boolean).map(value => [value.handle, value])).values()];
      const loaded = product?.handle === context.productHandle ? { catalog: api.createCatalog(config, products, context) } : await controller.load();
      const mapped = loaded.catalog.variants.filter(variant => storefrontHandle(variant) === context.productHandle && variant.availability !== 'unavailable');
      const v2 = api.isV2?.(config);
      const variants = v2 ? [...new Set(mapped.map(variant => variant.colorKey))].map(color => {
        const choices = api.choicesFor(loaded.catalog, color).filter(variant => mapped.includes(variant));
        return choices.find(variant => ['available', 'unknown'].includes(variant.availability)) || choices[0];
      }).filter(Boolean) : mapped;
      for (const form of standardForms) {
        const wrapper = form.closest('product-info');
        wrapper?.querySelectorAll('variant-selects').forEach(element => { element.hidden = true; });
        const bottle = form.closest('polygons-bottle');
        bottle?.querySelectorAll('[data-bottle-swatches]').forEach(element => { element.hidden = true; });
        const idInput = form.querySelector('[name="id"]');
        if (!idInput) continue;
        let poolInput;
        if (v2) {
          poolInput = form.querySelector('[name="properties[_polygons_stock_pool]"]');
          if (!poolInput) { poolInput = document.createElement('input'); poolInput.type = 'hidden'; poolInput.name = 'properties[_polygons_stock_pool]'; form.append(poolInput); }
        }
        const label = document.createElement('label'), select = document.createElement('select');
        const formId = (form.getAttribute('id') || 'product').replace(/[^a-zA-Z0-9_-]/g, '-');
        select.id = `native-option-${forms.indexOf(form)}-${formId}`; label.htmlFor = select.id; label.textContent = 'Availability and dispatch';
        select.style.cssText = 'width:100%;padding:12px;margin:8px 0 12px;border:1px solid #ccc;border-radius:8px;background:white;color:#161616';
        for (const variant of variants) {
          const option = document.createElement('option'); option.value = variant.poolKey || variant.variantId;
          const availabilityLabel = variant.availability === 'available' ? 'In stock' : variant.availability === 'sold-out' ? 'Sold out' : 'Unavailable';
          option.textContent = `${variant.title || variant.colorKey} · ${variant.pool === 'backorder' ? `Ships ${variant.dispatchDate}${variant.availability === 'available' ? '' : ` · ${availabilityLabel}`}` : availabilityLabel}`;
          option.disabled = variant.availability !== 'available'; select.append(option);
        }
        const current = variants.find(variant => variant.variantId === idOf(idInput.value) && variant.availability === 'available');
        const initial = current || variants.find(variant => variant.availability === 'available');
        select.value = initial?.poolKey || initial?.variantId || '';
        form.prepend(label, select);
        const update = () => {
          const chosen = variants.find(variant => (variant.poolKey || variant.variantId) === select.value);
          idInput.value = chosen?.variantId || ''; idInput.disabled = false;
          if (poolInput) poolInput.value = chosen?.stockPool || 'ready';
          bottle?.chooseVariant?.(idInput.value);
          try {
            const selection = selectionFromEntries([...new FormData(form).entries()], config);
            const plan = api.planCart(loaded.catalog, [selection], 'together');
            controls(form, false);
            shownSelections.set(form, { id: selection.id, acceptedDispatchDate: selection.acceptedDispatchDate });
            notice(form, `${new Intl.NumberFormat(context.locale || 'en', { style: 'currency', currency: context.currencyCode }).format(plan.merchandiseCents / 100)} for this selection. ${selection.acceptedDispatchDate ? `Ships ${selection.acceptedDispatchDate}.` : context.countryCode === 'US' ? 'Ships in 3-5 business days.' : 'In stock.'}`);
          } catch (cause) { shownSelections.delete(form); controls(form, true); notice(form, cause.message, true); }
        };
        select.addEventListener('change', update);
        document.addEventListener('input', event => { if (event.target.name === 'quantity' && event.target.form === form) update(); });
        update();
      }
    } catch (cause) { for (const form of standardForms) { controls(form, true); notice(form, cause.message || 'Availability is temporarily unavailable.', true); } }
  };
  document.addEventListener('submit', event => {
    const form = event.target;
    if (form?.tagName !== 'FORM' || isProductForm(form) || ['PolygonsProductForm', 'PolygonsReadyPaymentForm'].includes(form.getAttribute('id'))) return;
    const action = new URL(form.action, location.href).pathname;
    if (/\/(?:cart|checkout)(?:[/.?]|$)/.test(action) && (event.submitter?.name === 'checkout' || /\/checkout/.test(action))) {
      event.preventDefault(); event.stopImmediatePropagation(); openCart(null, event.submitter);
    }
  }, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('a[href],[data-open="cart"],[data-cart-open],[data-checkout],.shopify-payment-button,[data-native-accelerated-wallets]');
    if (!trigger) return;
    const wallet = trigger.closest('[data-native-accelerated-wallets]');
    if (wallet && window.PolygonsNativeWallets?.allow(wallet) === true) return;
    if (trigger.hasAttribute('data-native-cart-page') || trigger.matches('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) return;
    const href = trigger.getAttribute('href') || '';
    if (!trigger.matches('a') || /\/(?:cart|checkout|checkouts)(?:[/.?]|$)/.test(new URL(href, location.href).pathname)) {
      if (!wallet && trigger.closest('[data-native-cart]')) return;
      const readyForm = trigger.closest('#PolygonsReadyPaymentForm');
      if (trigger.matches('.shopify-payment-button') && trigger.closest('[data-native-ready-payment][data-native-ready="true"]')
        && window.PolygonsNativeProductPage?.allowReadyPayment?.(readyForm) === true) return;
      event.preventDefault(); event.stopImmediatePropagation(); openCart(null, trigger);
    }
  }, true);
  initializeProduct();
})();
